import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, FolderKanban, Wrench, Eye, Edit, Trash2 } from 'lucide-react';
import { FilterBuilder } from '@/components/invoices/FilterBuilder';
import { Link, useNavigate } from 'react-router-dom';
import { useProjectData, type Project } from './hooks/useProjectData';
import { useProjectFilterBuilder } from './hooks/useProjectFilterBuilder';
import { useNotify } from '@/hooks/useNotify';
import { useViewPreference } from '@/hooks/useViewPreference';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import ViewToggle from '@/components/shared/ViewToggle';
import FilterToolbar from '@/components/shared/FilterToolbar';
import ProjectsTable from '@/components/projects/ProjectsTable';
import ProjectsCardView from '@/components/projects/ProjectsCardView';

const ProjectsListPage = () => {
  console.log("[ProjectsListPage] Component mounted");
  const notify = useNotify();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const { viewMode, setViewMode } = useViewPreference({ entityType: 'projects', defaultView: 'grid' });

  // Use the new hooks
  const {
    projects,
    fetchProjects,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  } = useProjectData();
  
  const {
    filteredProjects,
    draftGroups,
    appliedGroups,
    filterFields,
    filterEntities,
    filterAttributesByEntity,
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
  } = useProjectFilterBuilder(projects);

  useEffect(() => {
    const loadProjects = async () => {
      setIsLoading(true);
      await fetchProjects();
      setIsLoading(false);
    };
    loadProjects();
  }, []);

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      console.log(`[deleteProjectMutation] Attempting to delete project: ${projectId}`);
      
      // First, delete all associated project documents
      const { error: docsError } = await supabase
        .from('project_documents')
        .delete()
        .eq('project_id', projectId);
        
      if (docsError) {
        console.error('Error deleting project documents:', docsError);
        throw docsError;
      }
      
      // Then delete the project
      const { error: projectError } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);
        
      if (projectError) {
        console.error('Error deleting project:', projectError);
        throw projectError;
      }
    },
    onSuccess: () => {
      console.log('[deleteProjectMutation] Project deleted successfully');
      notify.success('Project deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: (error) => {
      console.error('Error deleting project:', error);
      notify.error(error);
    }
  });

  const handleDeleteProject = (projectId: string) => {
    deleteProjectMutation.mutate(projectId);
  };

  const getProjectActions = (project: Project) => [
    {
      label: "View",
      icon: Eye,
      onClick: () => navigate(`/projects/${project.id}`),
    },
    {
      label: "Edit",
      icon: Edit,
      onClick: () => navigate(`/projects/${project.id}/edit`),
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: () => handleDeleteProject(project.id),
      destructive: true,
      requiresConfirmation: {
        title: "Delete Project?",
        description: `This will permanently delete "${project.name}" and all associated data. This action cannot be undone.`,
      },
    },
  ];

  return (
    <PageLayout>
      <PageHeader 
        title="Projects"
        description="Manage your construction projects"
      />
      
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              All Projects
            </CardTitle>
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
          
          {/* Unified FilterToolbar */}
          <FilterToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search projects..."
            sortOptions={[
              { value: 'created_at', label: 'Date Created' },
              { value: 'name', label: 'Project Name' },
              { value: 'expected_start_date', label: 'Start Date' },
              { value: 'expected_end_date', label: 'End Date' },
            ]}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            actions={
              <Button asChild size="sm" className="px-4">
                <Link to="/projects/create">
                  <Plus className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Create Project</span>
                  <span className="sm:hidden">Create</span>
                </Link>
              </Button>
            }
            advancedFilter={
              <FilterBuilder
                draftGroups={draftGroups}
                appliedGroups={appliedGroups}
                fields={filterFields}
                entities={filterEntities}
                attributesByEntity={filterAttributesByEntity}
                onAddGroup={addGroup}
                onUpdateGroup={updateGroup}
                onRemoveGroup={removeGroup}
                onAddRuleToGroup={addRuleToGroup}
                onUpdateRuleInGroup={updateRuleInGroup}
                onRemoveRuleFromGroup={removeRuleFromGroup}
                onClearAll={clearAllRules}
                onApplyFilters={applyFilters}
                hasActiveFilters={hasActiveFilters}
                hasUnappliedChanges={hasUnappliedChanges}
                canApplyFilters={canApplyFilters}
              />
            }
            hasAdvancedFilters={hasActiveFilters}
            advancedFilterCount={appliedGroups.reduce((c, g) => c + g.rules.filter(r => r.entity && r.attribute && r.value).length, 0)}
            onClearAdvancedFilters={clearAllRules}
          />
        </CardHeader>
        <CardContent className={viewMode === 'list' ? 'p-0' : undefined}>
          <DataTableWrapper
            isLoading={isLoading}
            isEmpty={!filteredProjects || filteredProjects.length === 0}
            emptyState={{
              title: "No projects found",
              description: hasActiveFilters || search 
                ? "No projects match your current search or filters." 
                : "Get started by creating your first project.",
              icon: <FolderKanban className="h-12 w-12 text-muted-foreground" />,
              action: (
                <Button asChild className="mt-4">
                  <Link to="/projects/create">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Project
                  </Link>
                </Button>
              )
            }}
          >
            {viewMode === 'list' ? (
              <ProjectsTable 
                projects={filteredProjects || []} 
                getProjectActions={getProjectActions} 
              />
            ) : (
              <ProjectsCardView 
                projects={filteredProjects || []} 
                getProjectActions={getProjectActions} 
              />
            )}
          </DataTableWrapper>
        </CardContent>
      </Card>

    </PageLayout>
  );
};

export default ProjectsListPage;
